/**
 * Coach Profile — Runtime Context
 *
 * In-memory accessor for the current CoachProfile + history snapshots so
 * scope orchestrators (setup/atm/day) can enrich observations with
 * temporal context (week-N, returning, persistent, new) WITHOUT having to
 * thread the profile down through every coach.analyze call site.
 *
 * Why a singleton:
 *   - Scopes are called from many places (SetupsTab, AtmTab, JournalView's
 *     LiveCoachFeed, useCoachProfile.rebuild, etc.). Plumbing the profile
 *     through every ctx would require touching all of them on every change.
 *   - The profile is a SHARED MENTAL MODEL — one trader, one mind. Multiple
 *     "live profiles" in the app would be a contradiction.
 *   - The hook (useCoachProfile) writes here on every state change, so the
 *     singleton stays in sync with what the dashboard renders.
 *
 * Pure read API: getRuntimeContext() never mutates, returns null/empty
 * when nothing has been pushed yet (analyzers handle that gracefully).
 */

let _profile = null;
let _history = [];

/**
 * Push the current profile + history into runtime. Called by useCoachProfile
 * whenever its state updates so downstream scopes always see the latest.
 */
export const setRuntimeContext = ({ profile = null, history = [] } = {}) => {
  _profile = profile;
  _history = Array.isArray(history) ? history : [];
};

/**
 * Read the current profile + history. Returns { profile: null, history: [] }
 * when nothing's been pushed — analyzers should treat that as "no temporal
 * context available, use canonical observation as-is."
 */
export const getRuntimeContext = () => ({
  profile: _profile,
  history: _history,
});

/**
 * Wipe the runtime context. Used by clearMemory in useCoachProfile so the
 * "no profile" state is consistent across both storage and runtime.
 */
export const clearRuntimeContext = () => {
  _profile = null;
  _history = [];
};

// Test-only: alias to wipe between assertions cleanly.
export const __resetForTests = clearRuntimeContext;
